import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";

import { useGetCommunityConfigurationQuery } from "@redux/portfolios/portfoliosApi";
import { BASE_PORTFOLIOS_PATH } from "@routing/Portfolios";

import { Button, Loader } from "@covertree/ui";
import { Toast } from "@covertree/utils";

import { CommunityConfigurationForm } from "./CommunityConfigurationForm";

const cardSx = {
  border: "1px solid",
  borderColor: "action.disabled",
  borderRadius: 2,
  p: 3,
};

export function CommunitiesConfiguration() {
  const navigate = useNavigate();
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const { data, error, isFetching, isLoading, refetch } =
    useGetCommunityConfigurationQuery(
      { portfolioId: portfolioId ?? "" },
      { skip: !portfolioId }
    );

  useEffect(() => {
    if (!portfolioId) {
      navigate(`/${BASE_PORTFOLIOS_PATH}`, { replace: true });
      return;
    }
    if (!data) return;
    if (data.portfolio.isActive) {
      Toast.showError("Cannot configure an active portfolio");
      navigate(`/${BASE_PORTFOLIOS_PATH}`, { replace: true });
      return;
    }
    if (!data.portfolio.integrationType) {
      Toast.showError("Cannot configure portfolio without integration");
      navigate(`/${BASE_PORTFOLIOS_PATH}`, { replace: true });
    }
  }, [data, navigate, portfolioId]);

  if (!portfolioId) return null;

  if (isLoading || (!data && isFetching)) {
    return (
      <Loader
        maxLoadTime={30}
        messages={["Loading community configuration…"]}
      />
    );
  }

  if (error || !data) {
    return (
      <Stack alignItems="flex-start" spacing={2} sx={cardSx}>
        <Alert severity="error">
          Unable to load the community configuration.
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => refetch()}>
            Retry
          </Button>
          <Button onClick={() => navigate(`/${BASE_PORTFOLIOS_PATH}`)}>
            Back to portfolios
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <CommunityConfigurationForm
      configuration={data}
      portfolioId={portfolioId}
    />
  );
}
